import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@/components/ui/icon';
import { useEscapeKey, useFocusTrap, usePresence } from '@/hooks';
import { cn } from '@/lib/cn';

export type DrawerWidth = 'sm' | 'md' | 'lg';

/* Almost the full screen on a phone, a panel from `sm` up. */
const WIDTHS: Record<DrawerWidth, string> = {
  sm: 'max-w-[92vw] sm:max-w-sm',
  md: 'max-w-[92vw] sm:max-w-md',
  lg: 'max-w-[92vw] sm:max-w-2xl',
};

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Pinned action bar at the bottom - typically Cancel plus a primary action. */
  footer?: ReactNode;
  width?: DrawerWidth;
}

/**
 * A panel that slides in from the right.
 *
 * WinDocs uses drawers and dedicated pages instead of modal dialogs; this is
 * the drawer half. It still behaves as a modal surface for assistive tech and
 * for the keyboard: focus is trapped, Escape closes, background scrolling is
 * locked, and the rest of the page is inert to screen readers.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  /* Held on screen through the slide-out. */
  const { mounted, entered } = usePresence(open);

  useEscapeKey(onClose, open);
  useFocusTrap(panelRef, open);

  /* Locking the body prevents the page behind from scrolling under the drawer.
     The scrollbar is replaced with equivalent padding so the layout does not
     shift sideways as it opens. */
  useEffect(() => {
    if (!open) {
      return;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [open]);

  if (!mounted) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={cn(
          'absolute inset-0 bg-navy-950/40 transition-opacity duration-panel',
          entered ? 'opacity-100 ease-enter' : 'opacity-0 ease-exit',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative flex h-full w-full flex-col border-l border-border bg-surface outline-none',
          /* Transform rather than width or position: it is the one property
             the compositor can animate without laying the page out again. */
          'transition-transform duration-panel will-change-transform',
          entered ? 'translate-x-0 ease-enter' : 'translate-x-full ease-exit',
          WIDTHS[width],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-content">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-content-muted">{description}</p>}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="-mr-1 shrink-0 rounded-sm p-1 text-content-muted hover:bg-surface-muted hover:text-content"
          >
            <Icon name="close" size={15} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-border bg-surface-muted px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
